import Paper from '@mui/material/Paper'
import Slide from '@mui/material/Slide'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { History } from 'history'
import { filter, find, map, pathOr, pick, propEq, without } from 'ramda'
import React from 'react'

import ContentLayout from '@/app/component/layout/content'
import PowerTable from '@/app/component/layout/power-table'
import SelectAll from '@/app/component/layout/table/select-all'
import { useDeepState } from '@/app/helpers'
import { CONTACTS_SIDEBAR_WIDTH } from '@/app/module/contacts/definitions'
import CreateGroupDialog from '@/app/module/groups/component/create-group-dialog'
import CreateSegmentDialog from '@/app/module/groups/component/create-segment-dialog'
import UpdateGroupDialog from '@/app/module/groups/component/update-group-dialog'
import UpdateSegmentDialog from '@/app/module/groups/component/update-segment-dialog'
import { useGetGroup } from '@/app/module/groups/hooks/use-get-group'
import { generateTableColumns } from '@/app/service/util/helpers'
import { Contact, ContactSelection, Filters } from '@/app/types/contact'
import { Column } from '@/config/types'

import Groups from './groups'
import ContactsTableToolbar from './toolbar'

type GroupDialog =
  | { type: 'create-group' }
  | { type: 'create-segment'; copyFrom?: number }
  | { type: 'edit'; groupType: 'groups' | 'segments'; id: number }

type Filter = { name: string; value: string }

type Query = {
  page: number
  size: number
  searchTerm: string
  filter: Filter
}

type DeleteContactsProps = {
  includedContactIds: number[]
  excludedContactIds: number[]
  allContactsAreSelected: boolean
  includedGroupIds: number[]
  search: string
}

type ActionProps = {
  totalRecipients: number
  recipients: Array<{ label: string; value: number }>
  contactIds: number[]
  excludeIds: number[]
  isAllContacts: boolean
  includedGroupIds: number[]
  search: string
}

export type AddContactsToGroupProps = ActionProps & {
  orgId: number
  token: string
}

export type RemoveContactsFromGroupProps = ActionProps

type SendMessageProps = ActionProps

type NavigateProps = Partial<Query> & {
  history: History
  replace?: boolean
}

type Props = {
  activeFilter: Filter
  data: Contact[]
  filters: Filters
  headers: Column[]
  history: History
  loading: boolean
  loadingItems: unknown[]
  orgId: number
  orgTotal: number
  page: number
  query: Query
  selection: ContactSelection
  timezone: string
  token: string
  total: number

  addContactsToGroup: (props: AddContactsToGroupProps) => void
  deleteItems: (props: DeleteContactsProps) => void
  downloadItems: () => void
  getContactsTableHeaders: (orgId: number) => Column[]
  getCustomFields: (payload: { token: string; orgId: number }) => Promise<void>
  getData: (payload: { token: string; orgId: number; query: Query }) => Promise<void>
  navigate: (props: NavigateProps) => void
  removeContactsFromGroup: (props: RemoveContactsFromGroupProps) => void
  saveContactsTableHeaders: (orgId: number, headers: Column[]) => void
  sendMessage: (props: SendMessageProps) => void
  setPageSize: (size: number) => void
}

export const removeHiddenColumns = (headers: Column[]): Column[] => filter((header) => header.visible, headers)

const ContactsTable: React.FC<Props> = (props) => {
  const {
    data,
    filters,
    history,
    loading,
    orgId,
    orgTotal,
    page,
    selection,
    timezone,
    token,
    total,
    addContactsToGroup,
    deleteItems,
    downloadItems,
    getContactsTableHeaders,
    getCustomFields,
    getData,
    navigate,
    removeContactsFromGroup,
    saveContactsTableHeaders,
    sendMessage,
    setPageSize,
  } = props

  const [dialog, setDialog] = React.useState<GroupDialog | null>(null)
  const [headers, setHeaders] = React.useState<Column[]>(props.headers)
  const [activeFilter] = useDeepState(props.activeFilter)
  const [query] = useDeepState(props.query)
  const { clearSelection } = selection

  const handleAddGroup = React.useCallback((groupType: 'groups' | 'segments') => {
    setDialog({ type: groupType === 'groups' ? 'create-group' : 'create-segment' })
  }, [])
  const handleCopySegment = React.useCallback((id: number) => {
    setDialog({ type: 'create-segment', copyFrom: id })
  }, [])
  const handleEditGroup = React.useCallback((groupType: 'groups' | 'segments', id: number) => {
    setDialog({ type: 'edit', groupType, id })
  }, [])
  const handleCloseDialog = React.useCallback(() => setDialog(null), [])
  const handleDeletedGroup = React.useCallback(
    (groupType: 'groups' | 'segments', id: number) => {
      if (activeFilter?.name !== groupType || Number(activeFilter.value) !== id) return

      clearSelection()
      navigate({ history, page: 1, replace: true, searchTerm: query.searchTerm })
    },
    [activeFilter, history, query.searchTerm, clearSelection, navigate],
  )

  React.useEffect(() => {
    setHeaders(props.headers)
  }, [props.headers])

  const contactFields: Column[] = React.useMemo(
    () => [
      {
        label: '',
        key: 'id',
        sticky: 'left',
        action: 'check',
        visible: true,
      },
      ...removeHiddenColumns(headers),
    ],
    [headers],
  )

  const columns = React.useMemo(() => generateTableColumns(contactFields, timezone), [contactFields, timezone])

  const [initialized, setInitialized] = React.useState<boolean>(false)
  const [groupsFilter, setGroupsFilter] = React.useState(true)
  const { data: groupItem } = useGetGroup(activeFilter?.value ? Number(activeFilter.value) : 0)

  const loadData = React.useCallback(async () => {
    try {
      const customFieldsPromise = getCustomFields({
        token,
        orgId,
      })
      await Promise.all([customFieldsPromise])
    } finally {
      setInitialized(true)
    }
  }, [orgId, token, getCustomFields])

  const reloadData = () => {
    loadData()
    getData({
      token,
      orgId,
      query,
    })
  }

  const ordered = React.useMemo(() => map((item) => item.id, data), [data])

  const getSelectedRecipients = () => {
    const selectedItems =
      selection.isAll || selection.groups.length > 0 || selection.search !== ''
        ? without(selection.list, ordered)
        : selection.list

    return (selectedItems as any).map((id: any) => {
      const found: any = find(propEq('id', id), data)
      return {
        label: pathOr('', ['fullPhoneNumber'], found),
        value: id,
      }
    })
  }

  const onFilterChange = React.useCallback(
    ({ name, value }: { name: string; value: string }) => {
      selection.clearSelection()
      if (name && value) {
        return navigate({
          history,
          ...pick(['size'], query),
          page: 1,
          filter: {
            name,
            value,
          },
        })
      }
      return navigate({
        history,
        ...pick(['page', 'size'], query),
      })
    },
    [history, query, selection, navigate],
  )
  const submitHandler = (term: string) => {
    navigate({ ...query, history, page: 1, searchTerm: term })
  }

  React.useEffect(() => {
    if (!token || !orgId) {
      return
    }
    getData({
      token,
      orgId,
      query,
    })
  }, [orgId, query, token, getData])

  React.useEffect(() => {
    if (!initialized && token && orgId) {
      loadData()
    }
  }, [initialized, orgId, token, loadData])

  React.useEffect(() => {
    if (orgId) {
      getContactsTableHeaders(orgId)
    }
  }, [orgId, getContactsTableHeaders])

  const selectAll = React.useMemo(
    () => (
      <SelectAll
        activeFilter={query.filter}
        filters={filters}
        ordered={ordered}
        orgTotal={orgTotal}
        pagination={{ total }}
        search={{ term: query.searchTerm }}
        selection={selection}
      />
    ),
    [filters, ordered, orgTotal, query.filter, query.searchTerm, selection, total],
  )

  const noData = React.useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '90px',
          width: '100%',
        }}
      >
        <Typography variant="h5" color="textSecondary">
          {query.searchTerm ? `No results for "${query.searchTerm}".` : 'No contacts were found.'}
        </Typography>
      </div>
    ),
    [query.searchTerm],
  )

  const tableStyle = React.useMemo(
    () => ({
      height: 'calc(100vh - 206px)',
      width: '100%',
      transition: 'width 0.3s',
      maxWidth: `calc(100vw - ${groupsFilter ? '322px' : '0px'})`,
      boxShadow: '0px 0px 0px 1px rgb(224 224 224)',
    }),
    [groupsFilter],
  )

  const handlePageChange = React.useCallback(
    (newPage: number) => {
      navigate({
        ...query,
        history,
        page: newPage,
      })
    },
    [history, query, navigate],
  )
  const handlePageSizeChange = React.useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize)
    },
    [setPageSize],
  )
  const handleRowAction = React.useCallback(
    (val: string | number) => {
      history.push(`/contacts/edit/${val}`, {
        contactsQuery: query,
        fromUrl: window.location.pathname + window.location.search,
      })
    },
    [history, query],
  )

  return (
    <div>
      <ContentLayout>
        <ContactsTableToolbar
          headers={headers}
          setHeaders={(customHeaders: Column[]) => {
            saveContactsTableHeaders(orgId, customHeaders)
          }}
          search={{
            term: query.searchTerm,
            onSubmit: (searchTerm: string) => submitHandler(searchTerm),
          }}
          loading={loading}
          selection={selection}
          toggleGroups={() => setGroupsFilter(!groupsFilter)}
          groupsFilter={groupsFilter}
          filters={{
            ...filters,
            data: {
              ...filters.data,
              groups: {
                ...filters.data.groups,
                values: [
                  {
                    ...groupItem,
                    value: groupItem?.type === 'NORMAL' ? `${groupItem.id}` : '',
                    label: groupItem?.type === 'NORMAL' ? groupItem?.name : '',
                    numberOfContacts: groupItem?.numberOfContacts || 0,
                  },
                ],
              },
              segments: {
                ...filters.data.segments,
                values: [
                  {
                    ...groupItem,
                    value: groupItem?.type === 'SMART' ? `${groupItem.id}` : '',
                    label: groupItem?.type === 'SMART' ? groupItem?.name : '',
                    numberOfContacts: groupItem?.numberOfContacts || 0,
                  },
                ],
              },
            },
          }}
          activeFilter={query.filter}
          reloadContacts={reloadData}
          onUploadContacts={() => history.push('/contacts/upload')}
          onAddContacts={() => {
            history.push('/contacts/add', {
              contactsQuery: query,
              fromUrl: window.location.pathname + window.location.search,
            })
          }}
          onAddGroups={() => setDialog({ type: 'create-group' })}
          onManageCustomFields={() => history.push('/organization/custom-fields')}
          onAddSegmentsFields={() => setDialog({ type: 'create-segment' })}
          onAddContactsToGroup={() =>
            addContactsToGroup({
              totalRecipients: selection.count,
              recipients: getSelectedRecipients(),
              contactIds:
                selection.isAll || selection.groups.length > 0 || selection.search !== '' ? [] : selection.list,
              excludeIds:
                selection.isAll || selection.groups.length > 0 || selection.search !== '' ? selection.list : [],
              isAllContacts: selection.isAll,
              includedGroupIds: selection.groups,
              search: selection.search,
              token,
              orgId,
            })
          }
          onFilterChange={({ name, value }: any = {}) => {
            onFilterChange({ name, value })
          }}
          onDeleteContacts={() =>
            deleteItems({
              includedContactIds: selection.isAll ? [] : selection.list,
              excludedContactIds: selection.isAll ? selection.list : [],
              allContactsAreSelected: selection.isAll,
              includedGroupIds: selection.groups,
              search: selection.search,
            })
          }
          onDownloadContacts={() => downloadItems()}
          onRemoveContactsFromGroup={() =>
            removeContactsFromGroup({
              totalRecipients: selection.count,
              recipients: getSelectedRecipients(),
              contactIds:
                selection.isAll || selection.groups.length > 0 || selection.search !== '' ? [] : selection.list,
              excludeIds:
                selection.isAll || selection.groups.length > 0 || selection.search !== '' ? selection.list : [],
              isAllContacts: selection.isAll,
              includedGroupIds: selection.groups,
              search: selection.search,
            })
          }
          onSendMessage={() =>
            sendMessage({
              totalRecipients: selection.count,
              recipients: getSelectedRecipients(),
              contactIds:
                selection.isAll || selection.groups.length > 0 || selection.search !== '' ? [] : selection.list,
              excludeIds:
                selection.isAll || selection.groups.length > 0 || selection.search !== '' ? selection.list : [],
              isAllContacts: selection.isAll,
              includedGroupIds: selection.groups,
              search: selection.search,
            })
          }
        />
      </ContentLayout>
      <Container>
        <Sidebar>
          <Slide direction="right" in={groupsFilter} mountOnEnter unmountOnExit>
            <SidebarContent elevation={4}>
              <Groups
                selected={query.filter?.value}
                onAdd={handleAddGroup}
                onCopy={handleCopySegment}
                onDeleted={handleDeletedGroup}
                onEdit={handleEditGroup}
                onFilterChange={onFilterChange}
              />
            </SidebarContent>
          </Slide>
        </Sidebar>

        {initialized && (
          <PowerTable
            columns={columns}
            data={data}
            noData={noData}
            page={page}
            pageSize={query.size}
            selectAll={selectAll}
            selection={selection}
            style={tableStyle}
            tableActions={null}
            total={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            rowAction={handleRowAction}
          />
        )}
      </Container>
      {dialog?.type === 'create-group' && <CreateGroupDialog open onClose={handleCloseDialog} />}
      {dialog?.type === 'create-segment' && (
        <CreateSegmentDialog copyFrom={dialog.copyFrom} open onClose={handleCloseDialog} />
      )}
      {dialog?.type === 'edit' && dialog.groupType === 'groups' && (
        <UpdateGroupDialog groupId={dialog.id} open onClose={handleCloseDialog} />
      )}
      {dialog?.type === 'edit' && dialog.groupType === 'segments' && (
        <UpdateSegmentDialog
          open
          segmentId={dialog.id}
          onClose={handleCloseDialog}
          onSaved={() => {
            if (activeFilter?.name === 'segments' && Number(activeFilter.value) === dialog.id) {
              getData({ token, orgId, query })
            }
          }}
        />
      )}
    </div>
  )
}

const Container = styled('div')(() => ({
  display: 'flex',
}))

const Sidebar = styled('div')(() => ({
  height: 'calc(100vh - 124px)',
  width: CONTACTS_SIDEBAR_WIDTH,
}))

const SidebarContent = styled(Paper)(() => ({
  minHeight: '60px',
  display: 'inline-block',
  background: 'white',
  verticalAlign: 'top',
  borderRight: '1px solid rgba(224, 224, 224, 1)',
  borderRadius: '0px',
  width: '100%',
}))

export default ContactsTable

export type { Filter as ContactsTableFilter, Props as ContactsTableProps, Query as ContactsTableQuery }
